import React, { useState, useEffect, useContext, useRef } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Zap, 
  FolderOpen,
  BookOpen,
  Table,
  Beaker,
  Dna,
  TestTube,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileCode
} from 'lucide-react';
import { getProject } from '../../../features/projects/lib/projects';
import type { Project } from '../../../shared/lib/supabase';
import { TabContext } from '../../contexts/TabContext';
import { useTeam } from '../../../features/teams/contexts/TeamContext';
import { useAuth } from '../../../features/auth/contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { supabase } from '../../../shared/lib/supabase';
import { CreateTeamModal } from '../../../features/teams/components/CreateTeamModal';


interface NavItem {
  label: string;
  icon?: React.ComponentType<any>;
  path?: string;
  exact?: boolean;
  indent?: boolean;
  isWorkflow?: boolean;
  isFilePreparer?: boolean;
  disabled?: boolean;
  isDivider?: boolean;
  isSection?: boolean;
  isToggle?: boolean; // For items that toggle sidebars
  toggleActive?: boolean; // Whether the toggle is active
}

interface SidebarProps {
  collapsed: boolean;
  useTabNavigation?: boolean;
  onTabOpen?: (type: string, data?: any) => void;
  onCollapsedChange?: (collapsed: boolean) => void;
  onToggleTablesSidebar?: () => void;
  showTablesSidebar?: boolean;
  onToggleEntriesSidebar?: () => void;
  showEntriesSidebar?: boolean;
  onToggleGenerationsSidebar?: () => void;
  showGenerationsSidebar?: boolean;
  onToggleFilePreparerSidebar?: () => void;
  showFilePreparerSidebar?: boolean;
  activeTabType?: string;
  lastInteraction?: { type: 'tab' | 'sidebar'; label?: string };
  onSidebarItemClick?: (label: string) => void;
}

export function Sidebar({ 
  collapsed, 
  useTabNavigation = false, 
  onTabOpen, 
  onCollapsedChange, 
  onToggleTablesSidebar, 
  showTablesSidebar,
  onToggleEntriesSidebar,
  showEntriesSidebar,
  onToggleGenerationsSidebar,
  showGenerationsSidebar,
  onToggleFilePreparerSidebar,
  showFilePreparerSidebar,
  activeTabType,
  lastInteraction,
  onSidebarItemClick
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentTeam } = useTeam();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  
  // Get active tab from context if available
  const tabContext = useContext(TabContext);
  const activeTab = tabContext?.activeTab;
  
  // Check if we're on a specific project page
  const projectMatch = location.pathname.match(/^\/projects\/([^\/]+)/);
  const isProjectDetailPage = !!projectMatch;
  const projectId = projectMatch?.[1];
  
  // Store project context in sessionStorage for project pages
  const storedProjectId = sessionStorage.getItem('currentProjectId');
  const isInProjectContext = isProjectDetailPage;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown]);

  // Listen for create team modal event
  useEffect(() => {
    const handleOpenCreateTeamModal = () => {
      setShowCreateTeamModal(true);
    };

    window.addEventListener('openCreateTeamModal', handleOpenCreateTeamModal);
    return () => {
      window.removeEventListener('openCreateTeamModal', handleOpenCreateTeamModal);
    };
  }, []);

  // Store project ID and fetch project details when we're on a project page
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (projectId && isInProjectContext) {
        try {
          const project = await getProject(projectId);
          setCurrentProject(project);
        } catch (error) {
          console.error('Error fetching project:', error);
          setCurrentProject(null);
        }
      } else {
        setCurrentProject(null);
      }
    };

    if (projectId) {
      sessionStorage.setItem('currentProjectId', projectId);
      fetchProjectDetails();
    } else if (!location.pathname.startsWith('/projects/')) {
      // Clear project context when navigating to any non-project page
      sessionStorage.removeItem('currentProjectId');
      setCurrentProject(null);
    }
  }, [projectId, isInProjectContext, location.pathname]);

  // Build navigation items based on context
  const buildNavItems = (): NavItem[] => {
    if (isInProjectContext) {
      // We're in a project context - show project navigation
      if (currentProject) {
        // Project data is loaded - show full navigation
        // Workflow items are now hidden - all projects use the same Protein Design workflow
        
        return [
          {
            label: 'Home',
            icon: Home,
            path: `/projects/${currentProject.id}`,
            exact: true
          },
          {
            label: 'Generations',
            icon: Zap,
            isToggle: true,
            toggleActive: showGenerationsSidebar || false
          },
          {
            label: 'Entries',
            icon: BookOpen,
            isToggle: true,
            toggleActive: showEntriesSidebar || false
          },
          {
            label: 'Tables',
            icon: Table,
            isToggle: true,
            toggleActive: showTablesSidebar || false
          },
          {
            label: 'Protein Design',
            icon: FileCode,
            isToggle: true,
            toggleActive: showFilePreparerSidebar || false
          },
          {
            label: 'Project Settings',
            icon: Settings,
            path: `/projects/${currentProject.id}/settings`
          }
        ];
      } else {
        // Project data is still loading - show minimal navigation with disabled items
        return [
          {
            label: 'Home',
            icon: Home,
            path: '#',
            exact: true,
            disabled: true
          },
          {
            label: 'Generations',
            icon: Zap,
            isToggle: true,
            disabled: true
          },
          {
            label: 'Entries',
            icon: BookOpen,
            isToggle: true,
            disabled: true
          },
          {
            label: 'Tables',
            icon: Table,
            isToggle: true,
            disabled: true
          },
          {
            label: 'Loading...',
            icon: Beaker,
            path: '#',
            disabled: true
          },
          {
            label: 'Project Settings',
            icon: Settings,
            path: '#',
            disabled: true
          }
        ];
      }
    } else {
      // Global navigation (for non-project pages)
      return [
        {
          label: 'Home',
          icon: Home,
          path: '/',
          exact: true
        }
      ];
    }
  };

  const navItems = buildNavItems();

  const handleNavigation = (item: NavItem, e: React.MouseEvent) => {
    // Prevent navigation for disabled items
    if (item.disabled) {
      e.preventDefault();
      return;
    }

    // Notify parent that a sidebar item was clicked
    if (onSidebarItemClick) {
      onSidebarItemClick(item.label);
    }

    // Handle toggle items (like Tables, Entries, Generations, JSON Creator)
    if (item.isToggle) {
      e.preventDefault();
      if (item.label === 'Tables' && onToggleTablesSidebar) {
        onToggleTablesSidebar();
      } else if (item.label === 'Entries' && onToggleEntriesSidebar) {
        onToggleEntriesSidebar();
      } else if (item.label === 'Generations' && onToggleGenerationsSidebar) {
        onToggleGenerationsSidebar();
      } else if (item.label === 'Protein Design' && onToggleFilePreparerSidebar) {
        onToggleFilePreparerSidebar();
      }
      return;
    }

    if (useTabNavigation && onTabOpen && isInProjectContext) {
      // Handle special cases for tab navigation
      if (item.label === 'Home') {
        e.preventDefault();
        onTabOpen('home');
      } else if (item.isWorkflow) {
        e.preventDefault();
        onTabOpen('chat');
      } else if (item.label === 'Generations' || item.label === 'Entries') {
        // For list pages, navigate normally
        return;
      } else if (item.label === 'Project Settings') {
        e.preventDefault();
        onTabOpen('settings');
      }
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="h-full flex flex-col pt-16">
      <div className="flex-1">

      {/* Navigation */}
      <nav className="space-y-1">
        {navItems.map((item, index) => {
          if (item.isDivider) {
            return <div key={`divider-${index}`} className="my-2 border-t border-gray-200" />;
          }

          if (item.isSection && !collapsed) {
            return (
              <div key={`section-${index}-${item.label}`} className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {item.label}
              </div>
            );
          }

          if (item.isSection && collapsed) {
            return null;
          }

          const Icon = item.icon!;
          const isDisabled = item.disabled || false;
          
          let isActive = false;
          
          if (!isDisabled) {
            // If last interaction was sidebar, highlight the clicked sidebar item
            if (lastInteraction?.type === 'sidebar') {
              isActive = lastInteraction.label === item.label;
            } 
            // If last interaction was tab, highlight based on active tab type
            else if (lastInteraction?.type === 'tab' && activeTabType) {
              // Map tab types to sidebar labels
              if (activeTabType === 'home' && item.label === 'Home') {
                isActive = true;
              } else if (activeTabType === 'settings' && item.label === 'Project Settings') {
                isActive = true;
              } else if (activeTabType === 'filepreparer' && item.label === 'Protein Design') {
                isActive = true;
              } else if (activeTabType === 'chat' && item.isWorkflow) {
                // Highlight the workflow item when chat tab is active
                isActive = Boolean(currentProject && 
                  ((item.label.includes('Enzyme') && currentProject.workflow_type === 'enzyme') ||
                   (item.label.includes('Antibody') && currentProject.workflow_type === 'antibody') ||
                   (item.label.includes('Peptide') && currentProject.workflow_type === 'peptide')));
              }
            }
            // Default to Home if no interaction yet
            else if (!lastInteraction && item.label === 'Home') {
              isActive = true;
            }
          }

          const linkContent = (
            <>
              <Icon className={`h-4 w-4 transition-colors duration-200 ${
                isActive ? 'text-gray-800' : 
                isDisabled ? 'text-gray-300' : 
                'text-gray-400 group-hover:text-gray-600'
              }`} />
              {!collapsed && (
                <div className="flex items-center justify-between flex-1">
                  <span className="text-base font-normal">{item.label}</span>
                  {item.isToggle && (
                    <ChevronDown className={`h-4 w-4 transition-all duration-200 ${
                      item.toggleActive 
                        ? 'opacity-100' 
                        : 'opacity-0 group-hover:opacity-100 group-hover:-rotate-180'
                    }`} />
                  )}
                </div>
              )}
            </>
          );

          const linkClassName = `
            flex items-center transition-all duration-200 group ${collapsed ? 'py-3.5 justify-center px-3' : 'py-2.5 gap-3 px-4'}
            ${item.indent && !collapsed ? 'ml-6' : ''}
            ${isActive
              ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900 shadow-sm' 
              : isDisabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }
          `;

          if (isDisabled) {
            return (
              <div
                key={`nav-${index}-${item.label}-disabled`}
                className={linkClassName}
                title={collapsed ? `${item.label} - Loading...` : 'Loading...'}
              >
                {linkContent}
              </div>
            );
          }

          // Render toggle items as buttons
          if (item.isToggle) {
            return (
              <button
                key={`nav-${index}-${item.label}`}
                className={`${linkClassName} w-full text-left`}
                title={collapsed ? item.label : undefined}
                onClick={(e) => handleNavigation(item, e)}
              >
                {linkContent}
              </button>
            );
          }

          return (
            <NavLink
              key={`nav-${index}-${item.label}`}
              to={item.path || '#'}
              className={linkClassName}
              title={collapsed ? item.label : undefined}
              onClick={(e) => handleNavigation(item, e)}
            >
              {linkContent}
            </NavLink>
          );
        })}
      </nav>
      </div>

      {/* Collapse button at the bottom */}
      {onCollapsedChange && (
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={() => onCollapsedChange(!collapsed)}
            className={`w-full flex items-center ${collapsed ? 'justify-center py-3.5' : 'justify-start gap-3 py-2.5'} px-3 text-gray-600 hover:bg-gray-50 rounded-sm transition-colors`}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span className="text-sm font-normal">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Create Team Modal */}
      <CreateTeamModal 
        isOpen={showCreateTeamModal} 
        onClose={() => setShowCreateTeamModal(false)} 
      />
    </div>
  );
}